"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PROJECT_INITIALIZED_REQUIRED_FILE_TOTAL = exports.PROJECT_INITIALIZED_REQUIRED_FILE_ENTRIES = exports.PROJECT_INITIALIZED_REQUIRED_FILES = exports.requiredFileTotal = exports.requiredFileEntries = exports.requiredFilesFor = exports.checklistGroupOf = exports.resolveRequirementsFor = exports.resolveRequiredFiles = exports.REQUIREMENT_STAGE_CHAIN = exports.FILE_REQUIREMENT_RULES = exports.ChecklistGroup = void 0;
const usage_1 = require("./usage");
/**
 * Groups the architect's synthesis checklist displays requirements under.
 *
 * Deliberately NOT `ProjectFileSection`: that taxonomy belongs to the documents
 * page (Administratif / Photos / Analyse urbaine / Matrice / Autres) and cannot
 * express this split — the five drawing views and the existing survey all
 * declare `PICTURES` there, and `DESIGN` is the generated-matrix section.
 */
exports.ChecklistGroup = {
    ADMIN: 'ADMIN',
    PLANS: 'PLANS',
    PHOTOS: 'PHOTOS',
};
exports.FILE_REQUIREMENT_RULES = [
    // Salesman intake set required to leave PROJECT_INITIALIZED (the dossier
    // "request" gate): the three dossier-photo sets, the client power of attorney,
    // the five project drawing views, the existing survey, and the commercial
    // dossier (#584). Applies to every project type and account today.
    //
    // Every count here is a MINIMUM, not a cap (see the interface doc above):
    // reaching it clears the gate, and further photos of the same kind are kept,
    // never rejected. The house and garden sets ask for 6; the street set asks for
    // only 3 (#583) — a single façade from the public way is enough context.
    {
        step: 'PROJECT_INITIALIZED',
        files: {
            [usage_1.ProjectFileUsage.HOUSE_PICTURE]: 6,
            [usage_1.ProjectFileUsage.STREET_PICTURE]: 3,
            [usage_1.ProjectFileUsage.GARDEN_PICTURE]: 6,
            [usage_1.ProjectFileUsage.CLIENT_PROXY]: 1,
            [usage_1.ProjectFileUsage.VIEW_TOP]: 1,
            [usage_1.ProjectFileUsage.VIEW_RIGHT]: 1,
            [usage_1.ProjectFileUsage.VIEW_LEFT]: 1,
            [usage_1.ProjectFileUsage.VIEW_FRONT]: 1,
            [usage_1.ProjectFileUsage.VIEW_3D_INSERTION]: 1,
            [usage_1.ProjectFileUsage.EXISTING_PLANS]: 1,
            [usage_1.ProjectFileUsage.SALES_FILE]: 1,
        },
        groups: {
            [usage_1.ProjectFileUsage.HOUSE_PICTURE]: exports.ChecklistGroup.PHOTOS,
            [usage_1.ProjectFileUsage.STREET_PICTURE]: exports.ChecklistGroup.PHOTOS,
            [usage_1.ProjectFileUsage.GARDEN_PICTURE]: exports.ChecklistGroup.PHOTOS,
            [usage_1.ProjectFileUsage.CLIENT_PROXY]: exports.ChecklistGroup.ADMIN,
            [usage_1.ProjectFileUsage.SALES_FILE]: exports.ChecklistGroup.ADMIN,
            [usage_1.ProjectFileUsage.VIEW_TOP]: exports.ChecklistGroup.PLANS,
            [usage_1.ProjectFileUsage.VIEW_RIGHT]: exports.ChecklistGroup.PLANS,
            [usage_1.ProjectFileUsage.VIEW_LEFT]: exports.ChecklistGroup.PLANS,
            [usage_1.ProjectFileUsage.VIEW_FRONT]: exports.ChecklistGroup.PLANS,
            [usage_1.ProjectFileUsage.VIEW_3D_INSERTION]: exports.ChecklistGroup.PLANS,
            [usage_1.ProjectFileUsage.EXISTING_PLANS]: exports.ChecklistGroup.PLANS,
            // Reminder-tier pieces a POOL adds below — grouped here so the group map
            // stays one list rather than being split across rules.
            [usage_1.ProjectFileUsage.PERSPECTIVE]: exports.ChecklistGroup.PLANS,
            [usage_1.ProjectFileUsage.IMPLANTATION]: exports.ChecklistGroup.PLANS,
        },
    },
    // A POOL dossier ("piscine") drops the drawing pieces that only make sense
    // for a built volume: no existing-state survey (relevé de l'existant), no
    // orthographic project views (top/right/left/front) and no 3D insertion. A
    // count of 0 removes each usage from the merged requirement, so neither the
    // backend send-gate nor the salesman document slots ask for them on a pool.
    // The commercial dossier is not blocking for a POOL either — it moves to the
    // reminder tier below, alongside the pool's own supporting pieces.
    {
        step: 'PROJECT_INITIALIZED',
        projectType: 'POOL',
        files: {
            [usage_1.ProjectFileUsage.EXISTING_PLANS]: 0,
            [usage_1.ProjectFileUsage.VIEW_TOP]: 0,
            [usage_1.ProjectFileUsage.VIEW_RIGHT]: 0,
            [usage_1.ProjectFileUsage.VIEW_LEFT]: 0,
            [usage_1.ProjectFileUsage.VIEW_FRONT]: 0,
            [usage_1.ProjectFileUsage.VIEW_3D_INSERTION]: 0,
            [usage_1.ProjectFileUsage.SALES_FILE]: 0,
        },
        // Surfaced, never blocking. Before #632 these three were a hardcoded
        // `if (project_type === 'POOL')` branch pushing warnings in BOTH
        // `checkProjectInitialized` and `checkProjectRequested`; declaring them here
        // deletes both branches and makes the tier visible to the frontend too.
        reminders: {
            [usage_1.ProjectFileUsage.PERSPECTIVE]: 1,
            [usage_1.ProjectFileUsage.SALES_FILE]: 1,
            [usage_1.ProjectFileUsage.IMPLANTATION]: 1,
        },
    },
    // No rule is declared for PROJECT_REQUESTED: it INHERITS the intake set whole
    // via REQUIREMENT_STAGE_CHAIN below. That is deliberate — the architect must
    // validate exactly what the commercial was required to supply (#632), and
    // restating the list here is what let the two drift in the first place. Add a
    // rule only to express a real DIFFERENCE.
    //
    // The `step` dimension accepts any ProjectStep, so later stages are appended
    // here once the architect fills the #393 questionnaire ("Droits d'accès et
    // fichiers requis par document"). Indicative mapping of that questionnaire's
    // progress stages to project steps:
    //   Demandé   → PROJECT_INITIALIZED (the intake/request gate above)
    //   Validé    → PROJECT_ACCEPTED
    //   En cours  → PROJECT_IN_PROGRESS
    //   Déposé    → PROJECT_FILE_SENT / PROJECT_FILE_SUBMITTED
    //   Terminé   → PROJECT_COMPLETED
    // Adding a stage means extending REQUIREMENT_STAGE_CHAIN too, or it inherits
    // nothing.
    // Example of a future per-stage rule (illustrative, not active):
    //   { step: 'PROJECT_FILE_SENT', files: { [ProjectFileUsage.DEPOSIT_RECEIPT]: 1 } },
];
/**
 * Ordered chain of stages that ACCUMULATE requirements. Resolving a stage in
 * this chain applies every rule for each entry up to and including it, so a
 * later stage starts from the earlier stage's resolved set and states only its
 * differences.
 *
 * This is the requirement progression, NOT the lifecycle graph — deliberately
 * not derived from `PROJECT_STEP_ORDER`, which ranks PROJECT_REFUSED between
 * REQUESTED and ACCEPTED. A refused dossier is back in the commercial's hands
 * and is gated on presence, not validation, so `checkProjectRefused` resolves
 * PROJECT_INITIALIZED explicitly and must not inherit the verification stage.
 *
 * A step absent from this chain inherits nothing and resolves only its own
 * rules, which keeps every historical single-step lookup unchanged.
 */
exports.REQUIREMENT_STAGE_CHAIN = [
    'PROJECT_INITIALIZED',
    'PROJECT_REQUESTED',
];
const ruleMatches = (rule, ctx) => {
    if (rule.step !== ctx.step)
        return false;
    if (rule.projectType !== undefined && rule.projectType !== ctx.projectType)
        return false;
    if (rule.accountType !== undefined && rule.accountType !== ctx.accountType)
        return false;
    return true;
};
/**
 * Merge one rule's count map into an accumulator. A 0 count is an explicit
 * opt-out: drop the usage so it surfaces neither in the gate check nor in the
 * requirement total.
 */
const mergeCounts = (into, from) => {
    for (const [usage, count] of Object.entries(from ?? {})) {
        if (count === 0) {
            delete into[usage];
        }
        else {
            into[usage] = count;
        }
    }
};
/**
 * Pure single-step resolver — merges every matching rule's `files` in
 * declaration order. Applies NO stage inheritance: it answers "what does this
 * one step declare", which is what the document-access rules need.
 */
const resolveRequiredFiles = (rules, ctx) => {
    const result = {};
    for (const rule of rules) {
        if (!ruleMatches(rule, ctx))
            continue;
        mergeCounts(result, rule.files);
    }
    return result;
};
exports.resolveRequiredFiles = resolveRequiredFiles;
/**
 * The stages whose rules contribute to `step`: the chain prefix up to and
 * including it, or just `step` itself when it is not a chain member.
 */
const contributingSteps = (step) => {
    const idx = exports.REQUIREMENT_STAGE_CHAIN.indexOf(step);
    return idx === -1 ? [step] : exports.REQUIREMENT_STAGE_CHAIN.slice(0, idx + 1);
};
/**
 * Full resolution for a stage, following {@link REQUIREMENT_STAGE_CHAIN}
 * inheritance: a later stage starts from the earlier stage's resolved set and
 * applies only its own differences.
 *
 * `blocking` and `reminders` are kept disjoint — a usage that a narrowing rule
 * moves to the reminder tier must be opted out of `files` (count 0) by that same
 * rule, which is how the POOL rule already sheds the commercial dossier.
 */
const resolveRequirementsFor = (ctx) => {
    const blocking = {};
    const reminders = {};
    const groups = {};
    for (const step of contributingSteps(ctx.step)) {
        for (const rule of exports.FILE_REQUIREMENT_RULES) {
            if (!ruleMatches(rule, { ...ctx, step }))
                continue;
            mergeCounts(blocking, rule.files);
            mergeCounts(reminders, rule.reminders);
            Object.assign(groups, rule.groups);
        }
    }
    // A usage cannot be both. `files` wins: a rule that means "reminder" opts the
    // usage out of `files` explicitly, so anything still blocking is intentional.
    for (const usage of Object.keys(blocking)) {
        delete reminders[usage];
    }
    return { blocking, reminders, groups };
};
exports.resolveRequirementsFor = resolveRequirementsFor;
/** Resolved checklist group for a usage; ADMIN when the rules declare none. */
const checklistGroupOf = (resolved, usage) => resolved.groups[usage] ?? exports.ChecklistGroup.ADMIN;
exports.checklistGroupOf = checklistGroupOf;
/**
 * The mandatory (blocking) files for a `(step, projectType?, accountType?)`
 * request, with stage inheritance applied. Reminder-tier entries never appear
 * here — every historical caller reads this to mean "what blocks the gate".
 */
const requiredFilesFor = (ctx) => (0, exports.resolveRequirementsFor)(ctx).blocking;
exports.requiredFilesFor = requiredFilesFor;
/** `[usage, expectedCount]` tuples — handy for iterating the gate checks. */
const requiredFileEntries = (map) => Object.entries(map);
exports.requiredFileEntries = requiredFileEntries;
/** Sum of the expected counts in a requirement map. */
const requiredFileTotal = (map) => Object.values(map).reduce((sum, count) => sum + (count ?? 0), 0);
exports.requiredFileTotal = requiredFileTotal;
// ── Backwards-compatible defaults ───────────────────────────────────────────
// The historic flat exports remain valid: they are the PROJECT_INITIALIZED set
// with no project-type / account-type narrowing (the base rule above), so every
// existing consumer keeps the same values.
exports.PROJECT_INITIALIZED_REQUIRED_FILES = (0, exports.requiredFilesFor)({ step: 'PROJECT_INITIALIZED' });
exports.PROJECT_INITIALIZED_REQUIRED_FILE_ENTRIES = (0, exports.requiredFileEntries)(exports.PROJECT_INITIALIZED_REQUIRED_FILES);
exports.PROJECT_INITIALIZED_REQUIRED_FILE_TOTAL = (0, exports.requiredFileTotal)(exports.PROJECT_INITIALIZED_REQUIRED_FILES);
//# sourceMappingURL=requirements.js.map